// // components/SimpleLocationMap.tsx
// import React from 'react';
// import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
// import L from 'leaflet';

// // Fix default marker icons
// delete (L.Icon.Default.prototype as any)._getIconUrl;
// L.Icon.Default.mergeOptions({
//   iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
//   iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
//   shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
// });

// interface SimpleLocationMapProps {
//   latitude: number | null;
//   longitude: number | null;
//   label?: string;
//   height?: string;
// }

// const SimpleLocationMap: React.FC<SimpleLocationMapProps> = ({ 
//   latitude, 
//   longitude, 
//   label = 'Location',
//   height = '200px'
// }) => {
//   if (!latitude || !longitude) return null;

//   return (
//     <div className="mt-2">
//       <div className="d-flex align-items-center justify-content-between mb-1">
//         <small className="text-muted">{label}</small>
//         <a
//           href={`https://www.openstreetmap.org/?mlat=${latitude}&mlon=${longitude}&zoom=15`}
//           target="_blank"
//           rel="noopener noreferrer"
//           className="btn btn-link btn-sm p-0"
//         >
//           Open in OpenStreetMap →
//         </a>
//       </div>
//       <div style={{ 
//         width: '100%', 
//         height, 
//         borderRadius: '8px',
//         overflow: 'hidden',
//         border: '1px solid #dee2e6'
//       }}>
//         <MapContainer
//           center={[latitude, longitude]}
//           zoom={15}
//           style={{ height: '100%', width: '100%' }}
//           scrollWheelZoom={false}
//         >
//           <TileLayer
//             attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
//             url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
//           />
//           <Marker position={[latitude, longitude]}>
//             <Popup>{label}</Popup>
//           </Marker>
//         </MapContainer>
//       </div>
//     </div>
//   );
// };

// export default SimpleLocationMap;

// components/SimpleLocationMap.tsx
import React, { useState, useEffect, useRef } from 'react';
import { Spinner } from 'reactstrap';
import { Map, Maximize2, Minimize2 } from 'react-feather';

interface SimpleLocationMapProps {
  latitude: number | null;
  longitude: number | null;
  label?: string;
  height?: string;
  showRadius?: boolean;
  radius?: number;
  zoom?: number;
}

const SimpleLocationMap: React.FC<SimpleLocationMapProps> = ({
  latitude,
  longitude,
  label = 'Location',
  height = '200px',
  showRadius = false,
  radius = 100,
  zoom = 15,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<any>(null);
  const markerRef = useRef<any>(null);
  const circleRef = useRef<any>(null);
  const [ready, setReady] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  // Inject Leaflet CSS once
  useEffect(() => {
    if (document.getElementById('leaflet-css')) return;
    const link = document.createElement('link');
    link.id = 'leaflet-css';
    link.rel = 'stylesheet';
    link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
    document.head.appendChild(link);
  }, []);

  // Initialize map
  useEffect(() => {
    let alive = true;
    (async () => {
      if (!containerRef.current || mapRef.current) return;
      if (!latitude || !longitude) return;

      const L = (await import('leaflet')).default;

      // Fix webpack icon paths
      delete (L.Icon.Default.prototype as any)._getIconUrl;
      L.Icon.Default.mergeOptions({
        iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
        iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
        shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
      });

      if (!alive || !containerRef.current) return;

      const map = L.map(containerRef.current, {
        center: [latitude, longitude],
        zoom: zoom,
        zoomControl: true,
        attributionControl: true,
        dragging: true,
        scrollWheelZoom: true,
        touchZoom: true,
        doubleClickZoom: true,
      });

      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '© OpenStreetMap contributors',
        maxZoom: 19,
      }).addTo(map);

      // Create custom marker icon (teardrop shape)
      const pinIcon = L.divIcon({
        className: '',
        html: `<div style="
          width:32px;height:32px;
          background:#0d6efd;border:3px solid #fff;
          border-radius:50% 50% 50% 0;transform:rotate(-45deg);
          box-shadow:0 4px 14px rgba(13,110,253,0.4);
          position:relative;
        "><div style="
          width:9px;height:9px;background:#fff;border-radius:50%;
          position:absolute;top:50%;left:50%;
          transform:translate(-50%,-50%) rotate(45deg);
        "></div></div>`,
        iconSize: [32, 32],
        iconAnchor: [16, 32],
      });

      const marker = L.marker([latitude, longitude], {
        icon: pinIcon,
        draggable: false,
        interactive: true,
      }).addTo(map);

      // Optional: Add circle/radius
      let circle = null;
      if (showRadius) {
        circle = L.circle([latitude, longitude], {
          radius: radius,
          color: '#0d6efd',
          fillColor: '#0d6efd',
          fillOpacity: 0.12,
          weight: 2,
          opacity: 0.55,
          interactive: false,
        }).addTo(map);
        circleRef.current = circle;
      }

      // Add popup on marker click
      marker.bindPopup(`
        <div style="text-align:center;padding:4px;">
          <strong>${label}</strong>
          <br/>
          <small>
            Lat: ${latitude.toFixed(6)}
            <br/>
            Lng: ${longitude.toFixed(6)}
          </small>
          <br/>
          <a 
            href="https://www.openstreetmap.org/?mlat=${latitude}&mlon=${longitude}&zoom=15"
            target="_blank"
            rel="noopener noreferrer"
            class="btn btn-sm btn-primary mt-1"
            style="font-size:11px;padding:2px 8px;"
          >
            View Full Map
          </a>
        </div>
      `);

      mapRef.current = map;
      markerRef.current = marker;
      if (circle) circleRef.current = circle;
      
      if (alive) {
        setReady(true);
        // Force resize after map is ready
        setTimeout(() => map.invalidateSize(), 150);
      }
    })();

    return () => {
      alive = false;
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
        markerRef.current = null;
        circleRef.current = null;
      }
    };
  }, [latitude, longitude, zoom, showRadius, radius, label]);

  // Sync props to map when they change
  useEffect(() => {
    if (!ready || !mapRef.current || !markerRef.current) return;
    if (!latitude || !longitude) return;

    const pos: [number, number] = [latitude, longitude];
    markerRef.current.setLatLng(pos);
    
    if (circleRef.current) {
      circleRef.current.setLatLng(pos);
      if (radius) {
        circleRef.current.setRadius(radius);
      }
    }
    
    mapRef.current.setView(pos, mapRef.current.getZoom(), { animate: true });
    
    // Update popup content
    markerRef.current.setPopupContent(`
      <div style="text-align:center;padding:4px;">
        <strong>${label}</strong>
        <br/>
        <small>
          Lat: ${latitude.toFixed(6)}
          <br/>
          Lng: ${longitude.toFixed(6)}
        </small>
        <br/>
        <a 
          href="https://www.openstreetmap.org/?mlat=${latitude}&mlon=${longitude}&zoom=15"
          target="_blank"
          rel="noopener noreferrer"
          class="btn btn-sm btn-primary mt-1"
          style="font-size:11px;padding:2px 8px;"
        >
          View Full Map
        </a>
      </div>
    `);

    // Force resize
    setTimeout(() => mapRef.current?.invalidateSize(), 100);
  }, [latitude, longitude, radius, ready, label]);

  // Handle resize when expanded/collapsed
  useEffect(() => {
    if (ready && mapRef.current) {
      setTimeout(() => mapRef.current?.invalidateSize(), 200);
    }
  }, [isExpanded, ready]);

  // Handle modal resize
  useEffect(() => {
    const handleResize = () => {
      if (ready && mapRef.current) {
        setTimeout(() => mapRef.current?.invalidateSize(), 50);
      }
    };

    window.addEventListener('resize', handleResize);
    
    // Observe container size changes
    const observer = new ResizeObserver(() => {
      if (ready && mapRef.current) {
        setTimeout(() => mapRef.current?.invalidateSize(), 50);
      }
    });

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => {
      window.removeEventListener('resize', handleResize);
      observer.disconnect();
    };
  }, [ready]);

  if (!latitude || !longitude) return null;

  const currentHeight = isExpanded ? '400px' : height;

  return (
    <div className="mt-2">
      <div className="d-flex align-items-center justify-content-between mb-1">
        <div className="d-flex align-items-center gap-2">
          <Map size={14} className="text-primary" />
          <small className="text-muted">{label}</small>
          <span className="badge bg-light text-dark" style={{ fontSize: '9px' }}>
            {latitude.toFixed(4)}, {longitude.toFixed(4)}
          </span>
        </div>
        <div className="d-flex gap-2 align-items-center">
          <button
            className="btn btn-link btn-sm p-0"
            onClick={() => setIsExpanded(!isExpanded)}
            title={isExpanded ? 'Minimize' : 'Expand'}
          >
            {isExpanded ? <Minimize2 size={14} /> : <Maximize2 size={14} />}
          </button>
          <a
            href={`https://www.openstreetmap.org/?mlat=${latitude}&mlon=${longitude}&zoom=15`}
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-link btn-sm p-0"
          >
            Open in Map →
          </a>
        </div>
      </div>

      <div 
        className="position-relative w-100" 
        style={{ 
          height: currentHeight,
          borderRadius: '8px',
          overflow: 'hidden',
          border: '1px solid #dee2e6',
          backgroundColor: '#f8f9fa',
          transition: 'height 0.3s ease'
        }}
      >
        <div 
          ref={containerRef} 
          className="w-100 h-100" 
          style={{ zIndex: 0 }} 
        />

        {showRadius && ready && (
          <div className="position-absolute bottom-0 start-0 m-2" style={{ zIndex: 999 }}>
            <div className="bg-white bg-opacity-90 border rounded-3 px-3 py-1.5 d-flex align-items-center gap-1 shadow-sm">
              <Map size={12} className="text-primary" />
              <span className="text-xs fw-bold text-secondary">{radius}m radius</span>
            </div>
          </div>
        )}

        {!ready && (
          <div className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center bg-light">
            <Spinner color="primary" size="sm" className="me-2" />
            <span>Loading map...</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default SimpleLocationMap;